import React, { useState, useRef } from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"
import { makeStyles } from "@material-ui/core/styles"
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline"

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
    width: "100%",
    maxWidth: "500px",
    borderRadius: "4px",
    overflow: "hidden",
    backgroundColor: "#000",
    [theme.breakpoints.down("sm")]: {
      width: "calc(100% + 88px)",
      maxWidth: "calc(100% + 88px)",
      marginLeft: "-44px",
      marginRight: "-44px",
      borderRadius: 0,
    },
  },
  aspect: {
    position: "relative",
    width: "100%",
    paddingTop: "125%", // 4:5 portrait
  },
  poster: {
    position: "absolute !important",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  video: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "block",
    backgroundColor: "#000",
  },
  playButton: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover, &:focus-visible": {
      color: "#929B91",
    },
    "&:hover $playIcon, &:focus-visible $playIcon": {
      transform: "scale(1.04)",
    },
    "&:focus-visible": {
      outline: "2px solid #fff",
      outlineOffset: "-4px",
    },
  },
  playIcon: {
    width: "30%",
    height: "30%",
    color: "inherit",
    filter: "drop-shadow(0 2px 12px rgba(0, 0, 0, 0.45))",
    transition: "transform 0.2s ease",
    [theme.breakpoints.down("sm")]: {
      width: "36%",
      height: "36%",
    },
  },
}))

const VideoBlock = ({
  posterFluid,
  posterAlt,
  mp4Src,
  webmSrc,
  title,
  description,
  playLabel,
  uploadDate,
  durationISO,
}) => {
  const classes = useStyles()
  const [started, setStarted] = useState(false)
  const videoRef = useRef(null)

  const { site } = useStaticQuery(graphql`
    query VideoBlockSiteUrl {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)
  const siteUrl = site.siteMetadata.siteUrl
  const absolute = (p) => (p.startsWith("http") ? p : `${siteUrl}${p}`)

  const handlePlay = () => {
    setStarted(true)
    // After React renders the video, kick off playback.
    requestAnimationFrame(() => {
      if (videoRef.current) {
        const playPromise = videoRef.current.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {})
        }
      }
    })
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title,
    description: description,
    thumbnailUrl: [absolute(posterFluid.src)],
    uploadDate: uploadDate,
    duration: durationISO,
    contentUrl: absolute(mp4Src),
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <div className={classes.wrapper}>
        <div className={classes.aspect}>
          {!started && (
            <>
              <Img
                fluid={posterFluid}
                alt={posterAlt}
                className={classes.poster}
                imgStyle={{ objectFit: "cover" }}
              />
              <button
                type="button"
                className={classes.playButton}
                onClick={handlePlay}
                aria-label={playLabel}
              >
                <PlayCircleOutlineIcon
                  className={classes.playIcon}
                  aria-hidden="true"
                />
              </button>
            </>
          )}
          {started && (
            <video
              ref={videoRef}
              className={classes.video}
              controls
              playsInline
              preload="metadata"
              poster={posterFluid.src}
              aria-label={title}
            >
              <source src={webmSrc} type="video/webm" />
              <source src={mp4Src} type="video/mp4" />
            </video>
          )}
        </div>
      </div>
    </>
  )
}

VideoBlock.propTypes = {
  posterFluid: PropTypes.object.isRequired,
  posterAlt: PropTypes.string.isRequired,
  mp4Src: PropTypes.string.isRequired,
  webmSrc: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  playLabel: PropTypes.string.isRequired,
  uploadDate: PropTypes.string.isRequired,
  durationISO: PropTypes.string.isRequired,
}

export default VideoBlock
